import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserEditModal = ({ isOpen, onClose, onSave, user = null }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'agent',
    isActive: true
  });
  const [isSaving, setIsSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear validation error for this field when user starts typing
    if (validationErrors?.[field]) {
      setValidationErrors({ ...validationErrors, [field]: null });
    }
  };

  const handleBlur = (field) => {
    // Optional: Add validation logic on blur if needed
    const errors = {};
    if (field === 'fullName' && !formData?.fullName) {
      errors.fullName = 'الاسم الكامل مطلوب';
    }
    if (field === 'email' && !formData?.email) {
      errors.email = 'البريد الإلكتروني مطلوب';
    }
    setValidationErrors({ ...validationErrors, ...errors });
  };

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user?.full_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        role: user?.role || 'agent',
        isActive: user?.is_active !== false
      });
    } else {
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        role: 'agent',
        isActive: true
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.fullName || !formData?.email) {
      alert('الرجاء إدخال الاسم والبريد الإلكتروني');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(formData);
    } finally {
      setIsSaving(false);
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'مدير' },
    { value: 'manager', label: 'مدير فرع' },
    { value: 'agent', label: 'وكيل عقاري' },
    { value: 'staff', label: 'موظف' }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4">
          {/* Black Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80"
          />

          {/* Modal - Compact Version */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Header - Compact */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                {user ? 'تعديل مستخدم' : 'إضافة مستخدم جديد'}
              </h2>
              <button
                onClick={onClose}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Form - Compact */}
            <form onSubmit={handleSubmit} className="p-4 space-y-3 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الاسم الكامل *
                </label>
                <input
                  type="text"
                  value={formData?.fullName || ''}
                  onChange={(e) => handleChange('fullName', e?.target?.value)}
                  onBlur={() => handleBlur('fullName')}
                  placeholder="أحمد محمد"
                  className="w-full h-10 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                />
                {validationErrors?.fullName && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.fullName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  البريد الإلكتروني *
                </label>
                <input
                  type="email"
                  value={formData?.email || ''}
                  onChange={(e) => handleChange('email', e?.target?.value)}
                  onBlur={() => handleBlur('email')}
                  placeholder="ahmed@example.com"
                  disabled={!!user}
                  className="w-full h-10 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                {validationErrors?.email && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.email}</p>
                )}
                {user && (
                  <p className="text-xs text-gray-500 mt-1">
                    لا يمكن تعديل البريد الإلكتروني
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رقم الهاتف
                </label>
                <input
                  type="tel"
                  value={formData?.phone || ''}
                  onChange={(e) => handleChange('phone', e?.target?.value)}
                  onBlur={() => handleBlur('phone')}
                  placeholder="+962 79 123 4567"
                  className="w-full h-10 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                />
                {validationErrors?.phone && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.phone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  الدور الوظيفي *
                </label>
                <select
                  value={formData?.role || 'agent'}
                  onChange={(e) => handleChange('role', e?.target?.value)}
                  onBlur={() => handleBlur('role')}
                  className="w-full h-10 px-3 py-2 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all bg-white"
                >
                  {roleOptions?.map((option) => (
                    <option key={option?.value} value={option?.value}>
                      {option?.label}
                    </option>
                  ))}
                </select>
                {validationErrors?.role && (
                  <p className="text-red-500 text-xs mt-1">{validationErrors?.role}</p>
                )}
              </div>

              {user && (
                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData?.isActive || false}
                    onChange={(e) => handleChange('isActive', e?.target?.checked)}
                    className="w-4 h-4 text-primary border-2 border-gray-300 rounded focus:ring-2 focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700 cursor-pointer">
                    حساب نشط
                  </label>
                </div>
              )}

              {/* Actions - Compact */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 h-10 text-sm"
                >
                  {isSaving ? 'جاري الحفظ...' : user ? 'تحديث' : 'إضافة'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSaving}
                  className="flex-1 h-10 text-sm"
                >
                  إلغاء
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UserEditModal;